/**
 * Comment list + post-reply mutation for the customer detail page.
 *
 * The list query polls every 15 seconds (matched to useTicketDetail) so
 * an agent reply shows up without manual reload. The mutation invalidates
 * both this list and the ticket detail because posting a reply can
 * transition the ticket status (waiting_customer -> open) server-side.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type { CommentCreateRequest, CommentResponse } from "@/types/api";

import { ticketDetailQueryKey } from "./useTicketDetail";

/**
 * PREFIX key for one ticket's comments, window-agnostic. The two
 * mutations that invalidate it - ``useAddComment`` below and
 * ``useUpdateMyTicket`` - keep using this; TanStack matches keys by
 * prefix, so both the windowed and the full-history entries refresh.
 */
export function ticketCommentsQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["tickets", "comments", ticketNumber] as const;
}

/** Exact key for one (ticket, window) pair. ``null`` = full history. */
export function ticketCommentsQueryKeyFor(
  ticketNumber: number,
  bodyWindow: number | null,
): readonly unknown[] {
  return [...ticketCommentsQueryKey(ticketNumber), bodyWindow] as const;
}

export interface UseTicketCommentsOptions {
  enabled?: boolean;
  refetchIntervalMs?: number | false;
  /** Load the BODY of only the newest N messages; older rows arrive as
   * metadata with ``body_omitted=true``. ``null`` / omitted sends no
   * param and gets the whole thread, which is the endpoint's original
   * behavior. A long email thread quotes itself, so the customer was
   * being sent megabytes of their own quoted history on every poll.
   * See plans/2026-08-25-customer-portal-comment-windowing.md. */
  bodyWindow?: number | null;
}

export function useTicketComments(
  ticketNumber: number,
  options: UseTicketCommentsOptions = {},
): UseQueryResult<CommentResponse[]> {
  const bodyWindow = options.bodyWindow ?? null;
  return useQuery({
    queryKey: ticketCommentsQueryKeyFor(ticketNumber, bodyWindow),
    enabled: options.enabled !== false,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    // Expanding the history swaps the key. Without this the thread
    // would blank to a spinner while the full payload is in flight.
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.get<CommentResponse[]>(
        `/tickets/${ticketNumber}/comments`,
        bodyWindow === null
          ? undefined
          : { params: { body_window: bodyWindow } },
      );
      return response.data;
    },
  });
}

export function useAddComment(
  ticketNumber: number,
): UseMutationResult<CommentResponse, Error, CommentCreateRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CommentCreateRequest) => {
      const response = await api.post<CommentResponse>(
        `/tickets/${ticketNumber}/comments`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketCommentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: ticketDetailQueryKey(ticketNumber),
      });
    },
  });
}
